import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft, IconSend, IconX } from "@tabler/icons-react";
import { mutate } from "swr";
import { mutateByPrefix } from "../../../../swr/useApi";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { Select } from "../../../../components/forms/select";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { ClaimStatusChip } from "../components/claim-status-chip";
import {
  useAdminWarrantyClaimDetail,
  resolveAdminClaim,
  addAdminClaimMessage,
  ADMIN_CLAIM_TRANSITIONS,
} from "../../hooks/use-admin-warranty-claims";
import type { WarrantyClaimMessageDto } from "../../../../api";

type ClaimWithResolution = {
  id: string;
  saleOrderId: number;
  claimType: string;
  description: string;
  status: string;
  attachments: Array<{ objectKey: string; fileName: string; contentType: string; url?: string } | string>;
  messages?: WarrantyClaimMessageDto[];
  createdAt: string;
  updatedAt: string;
  resolutionAction?: string | null;
  resolutionNote?: string | null;
};

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

const TYPE_LABEL: Record<string, string> = {
  DAMAGE: "Damage",
  DEFECT: "Defect",
  MISSING_PART: "Missing Part",
  DOES_NOT_MATCH_DESCRIPTION: "Does Not Match Description",
  OTHER: "Other",
};

const STATUS_LABEL: Record<string, string> = {
  NEW: "New",
  ACKNOWLEDGED: "Acknowledged",
  INVESTIGATING: "Investigating",
  MEDIATING: "Escalate to Mediation",
  RESOLVED: "Resolve",
  REJECTED: "Reject",
  ESCALATED: "Escalate",
};

const RESOLUTION_ACTION_OPTIONS = [
  { value: "REFUND", label: "Refund" },
  { value: "REPLACEMENT", label: "Replacement" },
  { value: "STORE_CREDIT", label: "Store Credit" },
  { value: "NONE", label: "None" },
];

export function AdminWarrantyClaimDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const cacheKey = id ? `admin-warranty-claim-${id}` : null;
  const { data: rawClaim, isLoading } = useAdminWarrantyClaimDetail(id ?? null);
  const claim = rawClaim as ClaimWithResolution | undefined | null;

  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [resolutionAction, setResolutionAction] = useState("");
  const [resolutionNote, setResolutionNote] = useState("");
  const [resolving, setResolving] = useState(false);

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  async function handleSendMessage() {
    if (!id || !replyText.trim()) return;
    setSending(true);
    try {
      await addAdminClaimMessage(id, replyText.trim());
      setReplyText("");
      await mutate(cacheKey);
    } finally {
      setSending(false);
    }
  }

  function startTransition(status: string) {
    setPendingStatus(status);
    setResolutionAction(status === "REJECTED" ? "NONE" : "");
    setResolutionNote("");
  }

  async function confirmTransition() {
    if (!id || !pendingStatus) return;
    setResolving(true);
    try {
      const updated = await resolveAdminClaim(id, {
        status: pendingStatus,
        ...(resolutionAction ? { resolutionAction } : {}),
        ...(resolutionNote.trim() ? { resolutionNote: resolutionNote.trim() } : {}),
      });
      if (updated) {
        setPendingStatus(null);
        await mutate(cacheKey);
        await mutateByPrefix("admin-warranty-claims");
        await mutateByPrefix("admin-warranty-claim-stats");
      }
    } finally {
      setResolving(false);
    }
  }

  if (isLoading || !claim) {
    return (
      <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
        <ShimmerLoader message="Loading claim details…" lines={8} />
      </div>
    );
  }

  const nextStatuses = ADMIN_CLAIM_TRANSITIONS[claim.status] ?? [];
  const messages = (claim.messages ?? []) as WarrantyClaimMessageDto[];
  const attachments = (claim.attachments ?? []) as Array<
    { objectKey: string; fileName: string; url?: string } | string
  >;

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(AppRoutes.ADMIN_WARRANTY_CLAIMS)}
          className="flex items-center gap-1 text-textSecondary hover:text-textDark transition-colors"
        >
          <IconArrowLeft size={18} />
          <Text variant="bodySm" tone="body">Back to warranty claims</Text>
        </button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <Heading variant="h2" tone="dark">Warranty Claim</Heading>
          <Text variant="bodySm" tone="muted" className="font-mono mt-0.5">{claim.id}</Text>
        </div>
        <ClaimStatusChip status={claim.status} />
      </div>

      {/* Claim info */}
      <div className="rounded-2xl border border-borderDefault bg-bgWhite p-6 flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Text variant="bodySmBold" tone="dark">Claim Type</Text>
            <Text variant="body" tone="body">{TYPE_LABEL[claim.claimType] ?? claim.claimType}</Text>
          </div>
          <div>
            <Text variant="bodySmBold" tone="dark">Sale Order</Text>
            <Text variant="body" tone="body">#{claim.saleOrderId}</Text>
          </div>
          <div>
            <Text variant="bodySmBold" tone="dark">Filed</Text>
            <Text variant="body" tone="body">{formatDate(claim.createdAt)}</Text>
          </div>
          <div>
            <Text variant="bodySmBold" tone="dark">Last Updated</Text>
            <Text variant="body" tone="body">{formatDate(claim.updatedAt)}</Text>
          </div>
        </div>
        <div>
          <Text variant="bodySmBold" tone="dark">Description</Text>
          <Text variant="body" tone="body" className="mt-1">{claim.description}</Text>
        </div>

        {/* Photo attachments */}
        {attachments.length > 0 && (
          <div>
            <Text variant="bodySmBold" tone="dark">Photos</Text>
            <div className="mt-2 flex flex-wrap gap-2">
              {attachments.map((att, i) => {
                if (typeof att === "string") {
                  return (
                    <div key={i} className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-borderDefault bg-bgLight">
                      <Text variant="bodyXs" tone="muted" className="text-center px-1">{att.split("/").pop()}</Text>
                    </div>
                  );
                }
                return att.url ? (
                  <button
                    key={att.objectKey}
                    type="button"
                    onClick={() => setLightboxUrl(att.url!)}
                    className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-borderDefault focus:outline-none focus:ring-2 focus:ring-brandPrimary"
                    title={att.fileName}
                  >
                    <img src={att.url} alt={att.fileName} className="h-full w-full object-cover" />
                  </button>
                ) : (
                  <div key={att.objectKey} className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-borderDefault bg-bgLight">
                    <Text variant="bodyXs" tone="muted" className="text-center px-1">{att.fileName}</Text>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Resolution summary (after resolved/rejected) */}
        {(claim.resolutionAction || claim.resolutionNote) && (
          <div className="grid grid-cols-2 gap-4 border-t border-borderDefault pt-4">
            {claim.resolutionAction && (
              <div>
                <Text variant="bodySmBold" tone="dark">Resolution Action</Text>
                <Text variant="body" tone="body">{claim.resolutionAction}</Text>
              </div>
            )}
            {claim.resolutionNote && (
              <div>
                <Text variant="bodySmBold" tone="dark">Resolution Note</Text>
                <Text variant="body" tone="body">{claim.resolutionNote}</Text>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Status transition panel */}
      {nextStatuses.length > 0 && (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite p-4 flex flex-col gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Text variant="bodySm" tone="body">Update status:</Text>
            {nextStatuses.map((s) => (
              <Button
                key={s}
                variant={pendingStatus === s ? "primary" : "secondary"}
                size="sm"
                disabled={resolving}
                onClick={() => startTransition(s)}
              >
                {STATUS_LABEL[s] ?? s}
              </Button>
            ))}
          </div>

          {pendingStatus && (
            <div className="flex flex-col gap-3 border-t border-borderDefault pt-3">
              {pendingStatus === "RESOLVED" && (
                <Select
                  name="resolutionAction"
                  label="Resolution action"
                  value={resolutionAction}
                  onChange={(e) => setResolutionAction(e.target.value)}
                  options={RESOLUTION_ACTION_OPTIONS}
                  density="dense"
                />
              )}
              <div className="flex flex-col gap-1.5">
                <Text variant="bodySmBold" tone="body">Resolution note (optional)</Text>
                <textarea
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  placeholder="Add a note visible to the buyer…"
                  rows={2}
                  className="w-full rounded-xl border border-borderDefault bg-bgLight px-4 py-3 text-sm text-textDark placeholder:text-textMuted resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" disabled={resolving} onClick={() => setPendingStatus(null)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" disabled={resolving} onClick={confirmTransition}>
                  {resolving ? "Saving…" : `Confirm — ${STATUS_LABEL[pendingStatus] ?? pendingStatus}`}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Message thread */}
      <div className="rounded-2xl border border-borderDefault bg-bgWhite flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-borderDefault">
          <Heading variant="h3" tone="dark">Message Thread</Heading>
        </div>

        {messages.length === 0 ? (
          <div className="px-6 py-8 flex items-center justify-center">
            <Text variant="body" tone="muted">No messages yet. Add the first response below.</Text>
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-borderDefault">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`px-6 py-4 ${m.authorRole === "admin" ? "bg-accentCyanTint/30" : ""}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Text variant="bodySmBold" tone="dark">
                    {m.authorRole === "admin" ? "You (Admin)" : m.authorRole === "seller" ? "Supplier" : "Buyer"}
                  </Text>
                  <Text variant="bodyXs" tone="muted">{formatDate(m.createdAt)}</Text>
                </div>
                <Text variant="body" tone="body">{m.content}</Text>
              </div>
            ))}
          </div>
        )}

        <div className="px-6 py-4 border-t border-borderDefault flex flex-col gap-3">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a response to the buyer…"
            rows={3}
            className="w-full rounded-xl border border-borderDefault bg-bgLight px-4 py-3 text-sm text-textDark placeholder:text-textMuted resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              leftIcon={<IconSend size={15} />}
              disabled={!replyText.trim() || sending}
              onClick={handleSendMessage}
            >
              {sending ? "Sending…" : "Send Response"}
            </Button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxUrl(null)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Close"
          >
            <IconX size={20} />
          </button>
          <img
            src={lightboxUrl}
            alt="Attachment"
            className="max-h-[90vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default AdminWarrantyClaimDetailPage;
